import React, { useState } from 'react';
import { ArrowLeft, Cookie, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCookies } from '../context/CookieContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

const LOGO_URL = 'https://www.sdtlab.it/assets/images/SDT_logo_2025_nobg.png';

const CookiesPage = () => {
  const { t } = useLanguage();
  const { cookieConsent, updateConsent, acceptAll, acceptNecessary } = useCookies();
  const [analytics, setAnalytics] = useState(cookieConsent?.analytics || false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateConsent({ analytics });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header */}
      <header className="fixed w-full z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="SDTLab Logo" className="h-14 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-gray-600 hover:text-amber-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.nav.home}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                <Cookie className="w-7 h-7 text-amber-700" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
                {t.cookiePolicy.title}
              </h1>
            </div>
            <p className="text-gray-500">
              {t.cookiePolicy.lastUpdate}: 1 Luglio 2025
            </p>
          </div>

          {/* Cookie Preferences */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-12 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">{t.cookies.customize}</h2>

            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl">
                <div>
                  <h3 className="font-semibold text-gray-800">Cookie Tecnici (Necessari)</h3>
                  <p className="text-sm text-gray-600 mt-1">Essenziali per il funzionamento del sito</p>
                </div>
                <Switch checked={true} disabled className="data-[state=checked]:bg-green-500" />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl">
                <div>
                  <h3 className="font-semibold text-gray-800">Cookie Analitici</h3>
                  <p className="text-sm text-gray-600 mt-1">Ci aiutano a migliorare l'esperienza utente</p>
                </div>
                <Switch
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  className="data-[state=checked]:bg-amber-600"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mt-8">
              <Button
                onClick={handleSave}
                className="bg-amber-700 hover:bg-amber-800 text-white"
              >
                {saved ? (
                  <><Check className="w-4 h-4 mr-2" /> Salvato!</>
                ) : (
                  'Salva preferenze'
                )}
              </Button>
              <Button
                onClick={acceptAll}
                variant="outline"
                className="border-amber-600 text-amber-700 hover:bg-amber-50"
              >
                {t.cookies.acceptAll}
              </Button>
              <Button
                onClick={acceptNecessary}
                variant="ghost"
                className="text-gray-600"
              >
                {t.cookies.necessary}
              </Button>
            </div>
          </div>

          {/* Policy Content */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-12">
            <p className="text-gray-600 mb-8 leading-relaxed">
              {t.cookiePolicy.intro}
            </p>

            <div className="space-y-10">
              {t.cookiePolicy.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    {section.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Smart Digital Technology LAB. {t.footer.rights}.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CookiesPage;